<template lang="pug">
  div(v-if="$store.state.trips" )
    #map(:style="{height:`${height-20}px`}")
    v-btn#placeButton2(fab color="primary" @click="togglePlaceMarkers")
      v-icon() {{(placeShowMarkers)? 'location_on':'location_off'}}
</template>
<script>
import mapboxgl from "mapbox-gl";
import { lineString } from "@turf/helpers";
import bbox from "@turf/bbox";
import centroid from "@turf/centroid";
const turf = { bbox, centroid, lineString };
import moment from "moment";

// The start/end callouts are permanent labels rather than click-to-open
// popups: no close button, and a map click must not dismiss them.
// focusAfterOpen would steal focus from the map canvas on every trip change.
const TRIP_LABEL_POPUP_OPTIONS = {
  offset: 25,
  closeButton: false,
  closeOnClick: false,
  focusAfterOpen: false
};

// Match the marker each label belongs to — primary for the start pin, accent
// for the end pin (src/plugins/vuetify.js).
const TRIP_LABEL_START_COLOR = "#ec037b";
const TRIP_LABEL_END_COLOR = "#63c5c7";

export default {
  props: {
    height: null
  },
  data: () => ({
    markerPng: "marker_green_translucent.png",
    mapLoaded: false,
    bbox: null,
    bboxOffset: 0.005,
    placeShowMarkers:false,
    geofencePopup:null,
    // add a line
  }),
  mounted() {
    this.map = null;
    this.$nextTick(() => {
      this.createMap();
    });
  },

  beforeDestroy() {
    this.removeAll();
    this.$nextTick(() => {
      if (this.map != null) {
        this.map.remove();
      }
    });
  },

  watch: {
    start_loc: function(value) {
      this.fromMarker.setLngLat(value);
    },

    end_loc: function(value) {
      this.toMarker.setLngLat(value);
    },

    the_path: function(value) {
      if (this.mapLoaded) {
         console.log(value);
        if(value.length == 0){
          return;
        }
        this.geoPath = turf.lineString(value);
        this.map.getSource("route_source").setData(this.geoPath);
        this.map.getSource("arrow_source").setData(this.geoPath);

        var bbox = turf.bbox(this.geoPath);

        bbox = [
          bbox[0] - this.bboxOffset,
          bbox[1] - this.bboxOffset,
          bbox[2] + this.bboxOffset,
          bbox[3] + this.bboxOffset
        ];
        this.map.fitBounds(bbox);
      } else {
        this.geoPath.data.geometry.coordinates = value;
      }

      // console.log(value);
    },

    start_time: function(value) {
      this.fromPopUp.setHTML(this.cleanUpStartLocationNames(value));
      this.showTripLabels();
    },

    end_time: function(value) {
      this.toPopUp.setHTML(this.cleanUpEndLocationNames(value));
      this.showTripLabels();
    }
  },

  computed: {


    start_loc() {
      return this.$store.getters["trips/getCurrentStartLocation"];
    },
    end_loc() {
      return this.$store.getters["trips/getCurrentEndLocation"];
    },
    the_path() {
      return this.$store.getters["trips/getPath"];
    },



    start_time() {
      if (this.$store.state.trips.currentIndex > -1) {
        return {
          time: moment(this.$store.getters["trips/getCurrentTrip"].min_time).format(
              "h:mm a, DD MMMM YYYY"
          ), obj: this.$store.getters["trips/getCurrentTrip"]
        }
      } else {
        return {time: "", obj: null}
      }

    },

    end_time() {

      if (this.$store.state.trips.currentIndex > -1) {
        return {
          time: moment(this.$store.getters["trips/getCurrentTrip"].max_time).format(
              "h:mm a")+ this.getDistance() +this.getTimeSpent(), obj: this.$store.getters["trips/getCurrentTrip"]
        }
      } else {
        return {time: "", obj: null}
      }
    },

  },

  methods: {

    // these used to pass a fresh arrow function to removeEventListener, which
    // never matches the registered handler, so nothing was ever unregistered
    removeAll:function(){
      this.unbindPlaceLink(this.fromPopUp, "#startLink", this.startLinkHandler);
      this.startLinkHandler = null;

      this.unbindPlaceLink(this.toPopUp, "#endLink", this.endLinkHandler);
      this.endLinkHandler = null;
    },

    unbindPlaceLink:function(popUp, selector, handler){
      if (popUp == null || handler == null || popUp._content == null) return;
      let link = popUp._content.querySelector(selector);
      if (link != null) {
        link.removeEventListener('click', handler);
      }
    },

    // Binds the "save as Place" button belonging to ONE popup.
    //
    // This used to querySelector the document for both #startLink and #endLink
    // and bind them both against whichever popup was passed in — so the end
    // button would create a Place at the start's coordinates. It went unnoticed
    // while only one popup could be open at a time; now that both endpoints
    // carry permanent labels, the lookup has to stay inside the popup's own DOM.
    loadNewPlace:function(target,context,store,router){
      let content = target != null ? target._content : null;
      if (content == null) return;

      let link = content.querySelector("#startLink") || content.querySelector("#endLink");
      if (link == null) return;

      let isStart = link.id === "startLink";
      let previous = isStart ? this.startLinkHandler : this.endLinkHandler;
      if (previous != null) {
        link.removeEventListener('click', previous);
      }

      let handler = function(){
        store.dispatch("place/addPlacePost",{
          name: target._content.lastElementChild.innerHTML,
          location: {lon: target._lngLat.lng, lat: target._lngLat.lat}
        }).then(function(){
          router.push({
            name: "placeDetailsForm",
            params: { placeID: store.getters["place/getCurrentPlace"].id }
          });
        })
      }

      link.addEventListener('click', handler);
      if (isStart) {
        this.startLinkHandler = handler;
      } else {
        this.endLinkHandler = handler;
      }
    },

    // A trip endpoint label: which end it is, when the vehicle was there, and
    // the place name. Styles are inline because the popup is written with
    // innerHTML and so never picks up this component's scoped CSS.
    //
    // A leading '%' on the location name means the backend matched a saved
    // Place. Without it the location is only a reverse-geocoded string, and the
    // pin button offers to save it as a Place — that button is what #startLink
    // and #endLink are wired to in loadNewPlace, so both ids must survive here.
    buildTripLabel: function(kind, value, name, placeFound) {
      let tag = kind === "start" ? "Start" : "End";
      let tagColor = kind === "start" ? TRIP_LABEL_START_COLOR : TRIP_LABEL_END_COLOR;
      let linkID = kind === "start" ? "startLink" : "endLink";

      let tagHTML =
        `<span style="display:inline-block;background:${tagColor};color:#ffffff;` +
        `font-size:10px;font-weight:700;letter-spacing:0.6px;text-transform:uppercase;` +
        `padding:1px 7px;border-radius:9px;">${tag}</span>`;

      let addPlaceHTML = placeFound
        ? ""
        : `<div id="${linkID}" title="Save this location as a Place" ` +
          `style="color:#ec037b;cursor:pointer;width:20px;height:20px;margin-left:6px">` +
          `<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">` +
          `<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />` +
          `<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg></div>`;

      // Deliberately three top-level siblings, not one wrapper: loadNewPlace
      // reads _content.lastElementChild.innerHTML as the name for the Place it
      // creates, so the name must be the last element and nothing else.
      return (
        `<div style="display:flex;align-items:center;justify-content:flex-start;text-align:left">${tagHTML}${addPlaceHTML}</div>` +
        `<div style="font-size:13px;font-weight:700;color:#0b0b0b;margin-top:3px;text-align:left">${value.time}</div>` +
        `<div style="font-size:11px;color:#666666;margin-top:1px;text-align:left">${name}</div>`
      );
    },

    // Strips the saved-Place marker off the raw location name.
    resolveLocationName: function(rawName) {
      let name = rawName || "";
      if (name.length > 0 && name[0] === "%") {
        return { name: name.substr(1, name.length - 1), placeFound: true };
      }
      return { name: name, placeFound: false };
    },

    cleanUpStartLocationNames:function(value){
      let resolved = this.resolveLocationName(value.obj != null ? value.obj.start_location_name : "");
      return this.buildTripLabel("start", value, resolved.name, resolved.placeFound);
    },

    cleanUpEndLocationNames:function(value){
      let resolved = this.resolveLocationName(value.obj != null ? value.obj.end_location_name : "");
      return this.buildTripLabel("end", value, resolved.name, resolved.placeFound);
    },

    // The labels are permanent, so they are opened as soon as a trip supplies
    // content rather than waiting for a click on the marker.
    //
    // Rebinding matters here: setHTML replaces the popup's DOM, which discards
    // the listener on #startLink/#endLink. Those were previously re-attached by
    // the popup's 'open' event, but a label that never closes never re-opens,
    // so selecting a second trip would leave the button dead.
    showTripLabels: function() {
      if (this.fromMarker != null && this.fromPopUp != null && !this.fromPopUp.isOpen()) {
        this.fromMarker.togglePopup();
      }
      if (this.toMarker != null && this.toPopUp != null && !this.toPopUp.isOpen()) {
        this.toMarker.togglePopup();
      }

      if (this.fromPopUp != null && this.fromPopUp.isOpen()) {
        this.loadNewPlace(this.fromPopUp, this, this.$store, this.$router);
      }
      if (this.toPopUp != null && this.toPopUp.isOpen()) {
        this.loadNewPlace(this.toPopUp, this, this.$store, this.$router);
      }
    },

    createMap: function() {
      mapboxgl.accessToken =
        "pk.eyJ1IjoibXVnYW1iaWtpbWF0aGkiLCJhIjoiY2p0OWNjaGZxMDdsdjQ0cXZjZTRhZnA0aSJ9.3b3qa7uHHInxWL3TBiMsSQ";
      this.map = new mapboxgl.Map({
        container: "map",
        style:  (this.$store.state.tracks.mapVectorStyle)?"mapbox://styles/mapbox/streets-v10":"mapbox://styles/mapbox/satellite-streets-v12",
        minzoom: 1.3,
        center: [36.8371433, -1.32593], // Nairobi
        zoom: 16,
        keyboard: false
      });
      const _canvas = this.map.getCanvas();
      _canvas.setAttribute('tabindex', '-1');
      _canvas.addEventListener('focus', (e) => {
        _canvas.blur();
        if (e.relatedTarget) e.relatedTarget.focus();
      });




      var el = document.createElement("div");
      el.className = "myMarker";
      el.setAttribute(
        "style",
        `background-image:url("/static/web/markers/${this.$store.state.tracks.mapVectorStyle?'marker_green_translucent.png':'marker_green_translucent_whitebg.png'}");width:80px;height:80px;background-size:80px 80px;background-position: center center;`
      );



      // Create the From Marker
      // add popup
      // No addTo here: it marks the popup open, which would make the first
      // togglePopup() in showTripLabels close it instead of opening it. The
      // marker owns the popup's lifecycle via setPopup below.
      this.fromPopUp = new mapboxgl.Popup(TRIP_LABEL_POPUP_OPTIONS);
      let context = this;
      let contextStore = this.$store;
      let contextRouter = this.$router;
      this.fromPopUp.on('open',function(e){
        context.loadNewPlace(e.target,context,contextStore,contextRouter);
      })

      this.fromPopUp.on('close',function(){
        context.removeAll();
      })

      // add marker
      this.fromMarker = new mapboxgl.Marker({
        color: this.$vuetify.theme.themes.light.primary
      });
      this.fromMarker.setLngLat([36.8371433, -1.32593]);
      this.fromMarker.setPopup(this.fromPopUp);
      this.fromMarker.addTo(this.map);

      // Create the Tp Marker
      // add popup
      this.toPopUp = new mapboxgl.Popup(TRIP_LABEL_POPUP_OPTIONS);
      this.toPopUp.on('open',function(e){
        context.loadNewPlace(e.target,context,contextStore,contextRouter);
      })

      this.toPopUp.on('close',function(){
        context.removeAll()
      })

      // add marker
      this.toMarker = new mapboxgl.Marker({
        color: this.$vuetify.theme.themes.light.accent
      });
      this.toMarker.setLngLat([38.8360433, -1.32593]);
      this.toMarker.setPopup(this.toPopUp);
      this.toMarker.addTo(this.map);

      this.geoPath = {
        type: "geojson",
        data: {
          type: "Feature",
          properties: {},
          geometry: {
            type: "LineString",
            coordinates: []
          }
        }
      };

      this.map.on("load", this.onLoadMap);
      this.map.on('moveend', () => this.map.getCanvas().blur());
      this.map.on('render', () => {
        if (document.activeElement === this.map.getCanvas()) {
          this.map.getCanvas().blur();
        }
      });

    },

    onLoadMap: function() {
      this.mapLoaded = true;

        // -- Geofence Areas
      const canvas = this.map.getCanvas();
      canvas.setAttribute('tabindex', '-1');
      canvas.addEventListener('focus', () => canvas.blur());

        this.map.addSource('geofence', {
            'type': 'geojson',
            'data': '/api/geofence/geoJSONCollection'
        });

        this.map.addLayer({
            'id': 'geofence_fill',
            'type': 'fill',
            'source': 'geofence', // reference the data source
            'layout': {},
            'paint': {
                'fill-color': '#0d97f7', // blue color fill
                'fill-opacity': 0.3,

            }
        });

        this.map.addLayer({
            'id': 'geofence_line',
            'type': 'line',
            'source': 'geofence', // reference the data source
            'layout': {},
            'paint': {
                "line-color": "#000f4e",
                "line-width": 2,

            }
        });

        this.map.on('mouseenter', 'geofence_fill', (e) => {

            // while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
            //     coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
            // }

            console.log(`mouse enter ${e.features[0].properties.name} at loc ${e.features[0].properties}`)
            let centroidCoord = [36.76132069660187, -1.2985446867349744]

            let theGeometry = JSON.parse(e.features[0].properties.geometry)
            if(theGeometry.type == "Point"){
                centroidCoord = theGeometry.coordinates;
            }else{
                let centroid = turf.centroid(e.features[0]);
                centroidCoord = centroid.geometry.coordinates;
            }


            this.geofencePopup = new mapboxgl.Popup({closeButton: false})
                .setLngLat(centroidCoord)
                .setHTML(`<i>Geofence:</i><br/><b>${e.features[0].properties.name}</b><br/>${e.features[0].properties.description??""}`)
            this.geofencePopup.addTo(this.map)
        });

        this.map.on('mouseleave', 'geofence_fill', () => {
            this.geofencePopup.remove()
        });


        //-- End Geofence Areas


      this.geoPath;
      this.map.addSource("route_source", this.geoPath);




      this.map.addLayer({
        id: "route_layer",
        type: "line",
        source: "route_source",
        layout: {
          "line-join": "round",
          "line-cap": "round"
        },
        paint: {
          "line-color": this.$vuetify.theme.themes.light.primary,
          "line-width": 4
        }
      });

      // add Arrow Source
      this.map.addSource('arrow_source',this.geoPath)
      let zMap = this.map
      this.map.loadImage("/static/web/arrow.png", function (err, image) {
        // Throw an error if something went wrong
        if (err) throw err;

        // Declare the image
        zMap.addImage("pattern", image);

        // Use it
        zMap.addLayer({
          id: "pattern-layer",
          type: "line",
          source: "arrow_source",
          paint: {
            "line-pattern": "pattern",
            "line-width": 20,
          },
        });
      });

      // initMap
      this.initialMap();


    },

    initialMap:function() {
      this.$store.dispatch("trips/loadPath");

    },

    showPlaces:function(){
      this.placeList = [];
      if(this.$store.state.place.currentIndex > -1) {
        this._iterateMaps(this.$store.state.place.place_data);
      }else{
          this.$store.dispatch("settings/groupSettings/loadAllGroups").then(()=> {
            this.$store.dispatch("place/loadAllPlaces").then(()=>{
              this._iterateMaps(this.$store.state.place.place_data);
          });
        });
      }
    },

    _iterateMaps(thePlaceArray){
      thePlaceArray.forEach((place) => {
        let group = this.$store.getters["settings/groupSettings/getGroupByID"](place.group_id) || {color:'#007900'};

        // create place marker
        let tempPlaceMarker = new mapboxgl.Marker({ color: group.color });
        tempPlaceMarker.setLngLat([place.location.lon, place.location.lat])
        // create popup
        let tempPopUp = new mapboxgl.Popup();
        tempPopUp.setHTML( `<span style="font-weight: bold"> ${place.name} </span>${(place.description != null)?'<br/><span>'+place.description+'</span>':''}  ${(place.street != null)?'<br/><span  style="color:#999999">'+place.street+'</span>':''}  ${(place.group_id != null)?'<br/><div style="color:#FFFFFF;background-color: #999999; padding: 0px 8px; border-radius: 6px">'+group.name+'</div>':''}`);

        // attach to popup
        tempPlaceMarker.setPopup(tempPopUp);

        // add marker and element to popup
        tempPlaceMarker.addTo(this.map);
        tempPlaceMarker.id = place.id;
        this.placeList.push(tempPlaceMarker)
      });
    },

    togglePlaceMarkers: function() {
      this.placeShowMarkers = !this.placeShowMarkers;
      if(this.placeShowMarkers == true){
        this.showPlaces();
      }else{
        this.placeList.forEach((placeMarker) => {
          placeMarker.remove();
        });
      }

    },

    getTimeSpent() {
      let microseconds = moment
        .utc(
          moment
            .duration(this.$store.getters["trips/getCurrentTrip"].timespent.Microseconds / 1000000, "seconds")
            .asMilliseconds()
        )


      let days =
        this.$store.getters["trips/getCurrentTrip"].timespent.Days > 0
          ? this.$store.getters["trips/getCurrentTrip"].timespent.Days + " Days + "
          : "";

      let months =
        this.$store.getters["trips/getCurrentTrip"].timespent.Months > 0
          ? this.$store.getters["trips/getCurrentTrip"].timespent.Months + " Months + "
          : "";

      let hours = microseconds.hours() > 0 ? " "+microseconds.hours()+"h" : ""
      let minutes = microseconds.minutes() > 0 || microseconds.hours() > 0 ? " " + microseconds.minute()+ "m":"";
      let seconds = microseconds.seconds() > 0 || microseconds.minutes() >0 || microseconds.hour() > 0 ? " " + microseconds.seconds()+"s":"";

      return " | " +months + days + hours + minutes + seconds;
    },

    getDistance() {
       return this.$store.getters["trips/getCurrentTrip"].distance > 0 ?" - "+ (this.$store.getters["trips/getCurrentTrip"].distance/1000).toFixed(2)+" km"  :"";
    }


  }
};
</script>
<style scoped lang="stylus">
#map {
}

#theMapContainer {
  height: 600px;
}

.myMarker {
  background-image: url('/static/web/markers/marker_green_translucent.png');
  background-size: cover;
  width: 75px;
  height: 75px;
  cursor: pointer;
}

.myPopUps {
  max-width: 200px;
}

.myPopUpsContent {
  text-align: center;
}

.mapboxgl-popup {
  max-width: 200px;
}

.mapboxgl-popup-content {
  text-align: center;
}


#placeButton2 {
  position: absolute;
  top:30px;
  right:30px;
}
</style>
